import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { TeamMember } from '../api/teamApi';

type RemoveMemberDialogProps = {
    member: TeamMember | undefined;
    isSubmitting: boolean;
    errorMessage: string | undefined;
    onConfirm: () => void;
    onCancel: () => void;
};

export function RemoveMemberDialog({
    member,
    isSubmitting,
    errorMessage,
    onConfirm,
    onCancel,
}: RemoveMemberDialogProps): ReactElement | null {
    const { t } = useTranslation();

    if (!member) {
        return null;
    }

    return (
        <Dialog
            open
            onClose={onCancel}
            aria-labelledby="remove-member-dialog-title"
            aria-describedby="remove-member-dialog-description">
            <DialogTitle id="remove-member-dialog-title">{t('SETTINGS.MEMBERS.REMOVE_DIALOG.TITLE')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <DialogContentText id="remove-member-dialog-description">
                        {t('SETTINGS.MEMBERS.REMOVE_DIALOG.BODY', { email: member.email })}
                    </DialogContentText>
                    {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} color="inherit">
                    {t('SETTINGS.MEMBERS.REMOVE_DIALOG.CANCEL')}
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={onConfirm}
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}>
                    {t('SETTINGS.MEMBERS.REMOVE_DIALOG.CONFIRM')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
